/**
 * Task Queue Context
 *
 * Provides shared SSE connection for real-time task queue updates.
 * Eliminates duplicate EventSource connections across components.
 *
 * Features:
 * - Single SSE connection shared across all consumers
 * - Automatic reconnection on disconnect (5 attempts)
 * - Fallback to polling if SSE fails
 * - Connection state tracking
 * - React Query cache synchronization
 * - Stream paused while the tab is hidden (CUR-1506): the provider wraps every
 *   admin route, so a background tab otherwise kept the server polling forever
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api/client';
import { queryKeys } from '@/services/query/query-client';

// Types for task queue data
export interface VideoQueueStats {
  queued: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  total: number;
}

export interface VideoQueueState {
  stats: VideoQueueStats;
  processing: Array<{
    content_id: string;
    video_id: number | null;
    user_id: string | null;
    queue_state: string;
    queue_position: number | null;
    calculated_timeout_seconds: number | null;
    created_at: string | null;
  }>;
  pending: Array<{
    content_id: string;
    video_id: number | null;
    user_id: string | null;
    queue_state: string;
    queue_position: number | null;
    calculated_timeout_seconds: number | null;
    created_at: string | null;
  }>;
  queued_preview: Array<{
    content_id: string;
    video_id: number | null;
    user_id: string | null;
    queue_state: string;
    queue_position: number | null;
    calculated_timeout_seconds: number | null;
    created_at: string | null;
  }>;
  constraints: {
    max_processing: number;
    max_pending: number;
  };
}

// Literal types for stricter type safety
type ContentType = 'article' | 'video';
type ProcessingStatus = 'pending' | 'processing' | 'completed' | 'failed';

// Sub-step progress tracking for video_outline stage
export interface SubStepProgress {
  stage: string;
  sub_step: string;
  display_name: string;
  description: string;
  status: 'in_progress' | 'completed';
  progress: number;
  cumulative_progress: number;
  updated_at: string;
}

/** Mirrors `ParkReason` in shared/queue_states.py (`ck_cps_park_reason`). */
export type ParkReason = 'transcript_batch' | 'call_batch' | 'source_inactive' | 'probe';

/**
 * A row parked in `awaiting_batch` (CUR-1629). It waits on a batch result and
 * holds no worker slot. Shape of `_fetch_parked_content_from_db` in task_utils.py.
 */
export interface ParkedContentItem {
  content_id: string;
  slug: string;
  title: string;
  content_type: string;
  queue_state: string;
  park_reason: ParkReason;
  parked_at: string | null;
  current_stage: string | null;
  url: string;
}

export interface TaskQueueData {
  active_tasks: Array<{
    content_id: string;
    slug: string;
    title: string;
    content_type: ContentType;
    processing_status: ProcessingStatus;
    url: string;
    updated_at: string | null;
    video_id: number | null;
    current_stage?: string;
    progress_percentage?: number;
    stages_completed?: number;
    stage_statuses?: Record<string, string>;
    current_sub_step?: SubStepProgress;
    vertex_batch?: import('@/types/batch-lane').VertexBatchLane;
  }>;
  recently_processed: Array<{
    content_id: string;
    slug: string;
    title: string;
    content_type: ContentType;
    processing_status: ProcessingStatus;
    queue_state?: string;
    cancel_reason?: string | null;
    url: string;
    updated_at: string | null;
    video_id: number | null;
  }>;
  queued_tasks: Array<{
    task_id: string;
    task_name: string;
    worker: string;
    eta: string | null;
    priority: number;
    content_id?: string;
    video_id?: number;
  }>;
  queued_content: Array<{
    content_id: string;
    slug: string;
    title: string;
    content_type: ContentType;
    processing_status: ProcessingStatus;
    queue_state: string;
    queue_position: number | null;
    url: string;
    updated_at: string | null;
    video_id: number | null;
    current_stage?: string;
    progress_percentage?: number;
    stages_completed?: number;
    stage_statuses?: Record<string, string>;
    current_sub_step?: SubStepProgress;
    vertex_batch?: import('@/types/batch-lane').VertexBatchLane;
  }>;
  /** Oldest 50 parked rows. Absent from the backend's error-fallback payloads. */
  parked_content?: ParkedContentItem[];
  metrics: {
    active_count: number;
    queued_count: number;
    /** True parked total, not capped by the 50-row `parked_content` page. */
    parked_count?: number;
    scheduled_count: number;
    reserved_count: number;
    recently_processed_count: number;
    next_scheduled_task: {
      content_id: string;
      eta: string;
      task_name: string;
    } | null;
  };
  video_queue: VideoQueueState | null;
  cached: boolean;
  timestamp?: string;
  sse?: boolean;
}

type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'polling';

interface TaskQueueContextType {
  // Data
  data: TaskQueueData | null;
  isLoading: boolean;

  // Connection state
  connectionState: ConnectionState;
  isConnected: boolean;
  isUsingSSE: boolean;
  lastUpdate: Date | null;
  error: Error | null;

  // Control methods
  reconnect: () => void;
  refresh: () => Promise<void>;
}

const TaskQueueContext = createContext<TaskQueueContextType | undefined>(undefined);

// Configuration
const FALLBACK_POLLING_INTERVAL = 5000;
const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_ATTEMPTS = 5;

interface TaskQueueProviderProps {
  children: ReactNode;
}

/**
 * Provider component for task queue SSE connection.
 * Manages a single shared EventSource connection.
 */
export function TaskQueueProvider({ children }: TaskQueueProviderProps) {
  const [data, setData] = useState<TaskQueueData | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [usePolling, setUsePolling] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const queryClient = useQueryClient();

  // Clean up SSE connection
  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Connect to SSE endpoint (async to resolve auth token)
  const connect = useCallback(async () => {
    cleanup();
    setConnectionState('connecting');

    try {
      // EventSource cannot send headers, so the stream takes a short-lived
      // ticket in the URL rather than the Auth0 token (CUR-2091). Each connect
      // and reconnect mints a fresh one.
      const baseUrl = import.meta.env.VITE_API_URL || '';
      const ticket = await apiClient.getTaskQueueStreamTicket();
      const sseUrl = `${baseUrl}/admin/task-queue/stream?ticket=${encodeURIComponent(ticket)}`;

      // The tab may have been hidden while the token resolved.
      if (document.hidden) {
        setConnectionState('disconnected');
        return;
      }

      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
        setConnectionState('connected');
        setError(null);
        setUsePolling(false);
        reconnectAttemptsRef.current = 0;
        console.log('[SSE] Connected to task queue stream');
      };

      eventSource.addEventListener('update', (event) => {
        try {
          const parsedData = JSON.parse(event.data) as TaskQueueData;
          setData(parsedData);
          setLastUpdate(new Date());

          // Sync to React Query cache for any components using useQuery directly
          queryClient.setQueryData(queryKeys.taskQueue(), parsedData);
        } catch (e) {
          console.error('[SSE] Failed to parse update event:', e);
        }
      });

      eventSource.addEventListener('heartbeat', () => {
        setLastUpdate(new Date());
      });

      eventSource.addEventListener('error', (event) => {
        try {
          const errorData = JSON.parse((event as MessageEvent).data);
          console.warn('[SSE] Error event:', errorData);
        } catch {
          // Not a JSON error event
        }
      });

      eventSource.onerror = () => {
        console.warn('[SSE] Connection error');
        setIsConnected(false);

        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          setConnectionState('reconnecting');

          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(
              `[SSE] Reconnecting (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})...`
            );
            connect();
          }, RECONNECT_DELAY);
        } else {
          console.log('[SSE] Max reconnect attempts reached, falling back to polling');
          setConnectionState('polling');
          setUsePolling(true);
          setError(new Error('SSE connection failed, using polling fallback'));
        }
      };
    } catch (e) {
      console.error('[SSE] Failed to create EventSource:', e);
      setConnectionState('polling');
      setUsePolling(true);
      setError(e instanceof Error ? e : new Error('Failed to connect to SSE'));
    }
  }, [cleanup, queryClient]);

  // Manual reconnect - resets attempts and tries SSE again
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    setUsePolling(false);
    connect();
  }, [connect]);

  // Refresh - force fetch fresh data
  const refresh = useCallback(async () => {
    try {
      // Bypass the server's status cache: refresh follows an action (cancel, reprocess).
      const freshData = await apiClient.getTaskQueueStatus({ fresh: true });
      setData(freshData as TaskQueueData);
      setLastUpdate(new Date());
      queryClient.setQueryData(queryKeys.taskQueue(), freshData);
    } catch (e) {
      console.error('[TaskQueue] Failed to refresh:', e);
    }
  }, [queryClient]);

  // Polling fallback using React Query
  const pollingQuery = useQuery({
    queryKey: queryKeys.taskQueue(),
    queryFn: () => apiClient.getTaskQueueStatus(),
    enabled: usePolling,
    refetchInterval: FALLBACK_POLLING_INTERVAL,
  });

  // Sync polling data to state
  useEffect(() => {
    if (usePolling && pollingQuery.data) {
      setData(pollingQuery.data as TaskQueueData);
      setLastUpdate(new Date());
    }
  }, [usePolling, pollingQuery.data]);

  // Connect on mount; close while the tab is hidden, reconnect when visible (CUR-1506)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        cleanup();
        setIsConnected(false);
        setConnectionState('disconnected');
      } else {
        reconnectAttemptsRef.current = 0;
        connect();
      }
    };

    if (!document.hidden) {
      connect();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cleanup();
    };
  }, [connect, cleanup]);

  const contextValue: TaskQueueContextType = {
    data: usePolling ? (pollingQuery.data as TaskQueueData | null) : data,
    isLoading: !data && !error,
    connectionState: usePolling ? 'polling' : connectionState,
    isConnected,
    isUsingSSE: !usePolling && isConnected,
    lastUpdate,
    error: usePolling ? (pollingQuery.error as Error | null) : error,
    reconnect,
    refresh,
  };

  return (
    <TaskQueueContext.Provider value={contextValue}>
      {children}
    </TaskQueueContext.Provider>
  );
}

/**
 * Hook to access task queue data and connection state.
 * Must be used within a TaskQueueProvider.
 */
// eslint-disable-next-line react-refresh/only-export-components -- non-component export alongside a component (affects dev fast-refresh only); file split is out of scope for CUR-1330
export function useTaskQueue(): TaskQueueContextType {
  const context = useContext(TaskQueueContext);

  if (!context) {
    throw new Error('useTaskQueue must be used within a TaskQueueProvider');
  }

  return context;
}
